import { useState } from "react";
import _compact from "lodash/compact";
import _map from "lodash/map";
import _flatten from "lodash/flatten";
import { useQuery } from "react-query";
import { FacetType, IFacet } from "shared-services";
import { FacetService } from "shared-services/src/services/facets";
import { config } from "../../../../config";
import { getFacetListByType } from "../../../Functional/helpers/getFacetListByType";
import {
  FilterConfig,
  FiltersFieldNames,
  IFilterValues,
  IUserFilters,
} from "../types";

const facetsAPI = new FacetService({ url: config.facetsApiUrl });

const GET_FILTER_FACETS = "GET_FILTER_FACETS";
const GET_FILTER_DATA = "GET_FILTER_DATA";

export function useFilters<T extends IFilterValues>(
  config: FilterConfig<T>
): IUserFilters<T> {
  const defaultValues = {
    ...config.reduce(
      (acc, { name, defaultValue }) => ({ ...acc, [name]: defaultValue }),
      {}
    ),
  } as T;
  const [values, setValues] = useState(defaultValues);
  const [filterDrawerValuesState, setFilterDrawerValuesState] =
    useState(defaultValues);

  const facetsCategories = _compact(_flatten(_map(config, "facetNames")));

  const filterDataConfig = config.reduce<{
    keys: FiltersFieldNames[];
    requests: (() => Promise<unknown>)[];
  }>(
    (acc, config) => {
      if (config?.requestDataFunc)
        return {
          ...acc,
          keys: [...acc.keys, config.name],
          requests: [...acc.requests, config.requestDataFunc],
        };

      return acc;
    },
    { keys: [], requests: [] }
  );

  const {
    data: facetData = [],
    isLoading: facetIsLoading,
    error: facetError,
  } = useQuery(
    [GET_FILTER_FACETS, facetsCategories],
    () => facetsAPI.getFacetsCategories(facetsCategories),
    { enabled: !!facetsCategories.length }
  );

  const {
    data: filterData = {} as Record<FiltersFieldNames, unknown>,
    isLoading: filterDataIsLoading,
    error: filterDataError,
  } = useQuery(
    [GET_FILTER_DATA, filterDataConfig.keys],
    async () => {
      const result = await Promise.all(
        filterDataConfig.requests.map((request) => request())
      );

      return filterDataConfig.keys.reduce(
        (acc, key, index) => ({ ...acc, [key]: result[index] }),
        {} as Record<FiltersFieldNames, unknown>
      );
    },
    {
      enabled: !!filterDataConfig.keys.length,
    }
  );

  const facets = facetsCategories.reduce(
    (acc, facet) => ({ ...acc, [facet]: getFacetListByType(facetData, facet) }),
    {} as Record<FacetType, IFacet[]>
  );

  const setFilterValues = (nextValues: Partial<T>) => {
    const filterValues = { ...values, ...nextValues };

    setValues(filterValues);
    setFilterDrawerValuesState(filterValues);
  };

  const setFilterDrawerValues = (nextValues: Partial<T>) =>
    setFilterDrawerValuesState({
      ...filterDrawerValuesState,
      ...nextValues,
    });

  const isLoading = facetIsLoading || filterDataIsLoading;
  const error = facetError || filterDataError;

  return {
    values,
    filterDrawerValues: filterDrawerValuesState,
    defaultValues,
    setFilterValues,
    setFilterDrawerValues,
    facets,
    filterData,
    isLoading,
    error,
    config,
  };
}
